import React from 'react';
import { useNotifications } from '../../context/NotificationContext';
import { Bell, AlertTriangle, CheckCircle, Info, ShieldAlert, X, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function getCategoryIcon(category: string, priority: string) {
  if (priority === 'urgent' || priority === 'high') {
    return <ShieldAlert size={18} style={{ color: '#ef4444' }} />;
  }
  switch (category) {
    case 'support':
      return <Info size={18} style={{ color: '#38bdf8' }} />;
    case 'editorial':
      return <CheckCircle size={18} style={{ color: '#10b981' }} />;
    case 'live_test':
      return <AlertTriangle size={18} style={{ color: '#f59e0b' }} />;
    default:
      return <Bell size={18} style={{ color: '#6366f1' }} />;
  }
}

export function ToastContainer() {
  const { toasts, dismissToast } = useNotifications();
  const navigate = useNavigate();

  if (!toasts || toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxWidth: '380px',
        width: 'calc(100vw - 48px)',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          style={{
            pointerEvents: 'auto',
            background: 'var(--color-surface, #1e293b)',
            color: 'var(--color-text, #f8fafc)',
            borderRadius: '12px',
            padding: '14px 16px',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.4), 0 8px 10px -6px rgba(0,0,0,0.3)',
            border: '1px solid var(--color-border, rgba(255,255,255,0.1))',
            backdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            animation: 'slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ marginTop: '2px', flexShrink: 0 }}>
            {getCategoryIcon(toast.category, toast.priority)}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-text)' }}>
                {toast.title}
              </h4>
              <span
                style={{
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  background: toast.priority === 'urgent' ? 'rgba(239, 68, 68, 0.2)' : 'var(--color-surface-soft, rgba(255,255,255,0.08))',
                  color: toast.priority === 'urgent' ? '#f87171' : 'var(--color-muted, #94a3b8)',
                  fontWeight: 600,
                }}
              >
                {toast.category}
              </span>
            </div>

            <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: 'var(--color-muted, #94a3b8)', lineHeight: 1.4 }}>
              {toast.body}
            </p>

            {toast.actionUrl && (
              <button
                onClick={() => {
                  dismissToast(toast.id);
                  if (toast.actionUrl?.startsWith('http')) {
                    window.open(toast.actionUrl, '_blank');
                  } else if (toast.actionUrl) {
                    navigate(toast.actionUrl);
                  }
                }}
                style={{
                  marginTop: '8px',
                  background: 'transparent',
                  border: 'none',
                  color: '#38bdf8',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: 0,
                }}
              >
                View Details <ExternalLink size={12} />
              </button>
            )}
          </div>

          <button
            onClick={() => dismissToast(toast.id)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-muted, #94a3b8)',
              cursor: 'pointer',
              padding: '2px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
